import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, X, Building2, Pencil, Trash2, ShieldCheck, Eye, DollarSign, FolderKanban, FileText, CreditCard } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useNavigate } from 'react-router-dom';

const CURRENCIES = {
  USD: '$',
  EGP: 'EGP ',
  EUR: 'EUR ',
  SAR: 'SAR ',
  AED: 'AED ',
};

const fmtMoney = (amount, currency = 'USD') =>
  `${CURRENCIES[currency] || '$'}${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const addMoney = (groups, currency, amount) => {
  const code = currency || 'USD';
  groups[code] = (groups[code] || 0) + Number(amount || 0);
};

const moneyGroupsText = (groups) => {
  const entries = Object.entries(groups).filter(([, amount]) => Math.abs(amount) > 0.001);
  return entries.length ? entries.map(([currency, amount]) => fmtMoney(amount, currency)).join(' / ') : fmtMoney(0);
};

const invoicePaidAmount = (invoice) => {
  const installments = invoice.installments || [];
  if (installments.length) {
    return installments
      .filter(inst => inst.status === 'paid')
      .reduce((sum, inst) => sum + Number(inst.amount || 0), 0);
  }
  return invoice.status === 'paid' ? Number(invoice.total || 0) : 0;
};

const invoiceRemainingAmount = (invoice) => Math.max(0, Number(invoice.total || 0) - invoicePaidAmount(invoice));

const ClientModal = ({ onClose, onSave, initial }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(
    initial
      ? {
          companyName: initial.companyName || '',
          contactPerson: initial.contactPerson || '',
          email: initial.email || '',
          phone: initial.phone || '',
          industry: initial.industry || '',
          notes: initial.notes || '',
          createAccount: false,
          resetPassword: false,
          portalPassword: '',
        }
      : { companyName: '', contactPerson: '', email: '', phone: '', industry: '', notes: '', createAccount: false, password: '' }
  );
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [servicePackages, setServicePackages] = useState([]);
  const [clientServices, setClientServices] = useState([{ serviceId: '', name: '', description: '', quantity: 1, unitPrice: '', currency: 'USD' }]);
  const [enablePaymentPlan, setEnablePaymentPlan] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState({
    serviceName: '',
    currency: 'USD',
    total: '',
    installments: [{ description: 'First Payment', amount: '', dueDate: '' }],
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setPlan = (k, v) => setPaymentPlan(p => ({ ...p, [k]: v }));
  const { showToast } = useToast();
  useEffect(() => {
    if (!isEdit) {
      Promise.all([axios.get('/api/services'), axios.get('/api/services/packages')])
        .then(([s, p]) => { setServiceCatalog(s.data.data.filter(x => x.isActive !== false)); setServicePackages(p.data.data.filter(x => x.isActive !== false)); })
        .catch(() => {});
    }
  }, [isEdit]);
  const servicesTotal = clientServices.reduce((sum, s) => sum + Number(s.quantity || 1) * Number(s.unitPrice || 0), 0);
  const planTotal = Number(paymentPlan.total || 0);
  const installmentsTotal = paymentPlan.installments.reduce((sum, inst) => sum + Number(inst.amount || 0), 0);
  const planDiff = planTotal - installmentsTotal;
  const planSymbol = CURRENCIES[paymentPlan.currency] || '$';

  const updateClientService = (i, key, value) => {
    setClientServices(rows => rows.map((row, idx) => {
      if (idx !== i) return row;
      if (key === 'serviceId') {
        const selected = serviceCatalog.find(s => s.id === value);
        return { ...row, serviceId: value, name: selected?.name || '', description: selected?.description || '', unitPrice: selected?.defaultPrice || '', currency: selected?.currency || row.currency };
      }
      return { ...row, [key]: value };
    }));
  };
  const addClientService = () => setClientServices(rows => [...rows, { serviceId: '', name: '', description: '', quantity: 1, unitPrice: '', currency: paymentPlan.currency || 'USD' }]);
  const removeClientService = (i) => setClientServices(rows => rows.filter((_, idx) => idx !== i));
  const useServicesAsPaymentTotal = () => setPaymentPlan(p => ({ ...p, total: servicesTotal.toFixed(2), serviceName: clientServices.map(s => s.name).filter(Boolean).join(', ') || p.serviceName }));
  const applyPackage = (packageId) => {
    const pack = servicePackages.find(p => p.id === packageId);
    if (!pack) return;
    setClientServices((pack.items || []).map(item => ({
      serviceId: item.serviceId || '',
      name: item.description || '',
      description: item.details || '',
      quantity: item.quantity || 1,
      unitPrice: item.unitPrice || 0,
      currency: pack.currency || 'USD',
    })));
    setPaymentPlan(p => ({ ...p, currency: pack.currency || p.currency, total: Number(pack.total || 0).toFixed(2), serviceName: pack.name }));
  };

  const updateInstallment = (i, key, value) => {
    setPaymentPlan(p => ({
      ...p,
      installments: p.installments.map((inst, idx) => idx === i ? { ...inst, [key]: value } : inst),
    }));
  };

  const addInstallment = () => {
    setPaymentPlan(p => ({
      ...p,
      installments: [...p.installments, { description: `Payment ${p.installments.length + 1}`, amount: '', dueDate: '' }],
    }));
  };

  const removeInstallment = (i) => {
    setPaymentPlan(p => ({ ...p, installments: p.installments.filter((_, idx) => idx !== i) }));
  };

  const splitEvenly = () => {
    if (!planTotal || !paymentPlan.installments.length) return;
    const each = (planTotal / paymentPlan.installments.length).toFixed(2);
    setPaymentPlan(p => ({ ...p, installments: p.installments.map(inst => ({ ...inst, amount: each })) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && enablePaymentPlan && !paymentPlan.installments.some(inst => Number(inst.amount) > 0 && inst.dueDate)) {
      showToast('Add at least one installment amount and due date', 'error');
      return;
    }
    try {
      const res = isEdit ? await axios.put(`/api/clients/${initial.id}`, {
        ...form,
        portalPassword: form.portalPassword || undefined,
      }) : await axios.post('/api/clients', {
        ...form,
        services: clientServices.filter(s => (s.name || '').trim() || s.serviceId),
        paymentPlan: enablePaymentPlan ? { ...paymentPlan, enabled: true } : { enabled: false },
      });
      if (res.data.portalPassword) {
        showToast(`Portal password for ${form.email}: ${res.data.portalPassword}`, 'success');
      } else {
        showToast(isEdit ? 'Client updated' : 'Client created', 'success');
      }
      onSave();
    } catch (err) { showToast(err.response?.data?.message || 'Error', 'error'); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Edit Client' : 'Add New Client'}</div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="grid-2">
              <div className="input-group"><label className="input-label">Company Name *</label><input className="input" required value={form.companyName} onChange={e => set('companyName', e.target.value)} placeholder="Acme Corp" /></div>
              <div className="input-group"><label className="input-label">Contact Person *</label><input className="input" required value={form.contactPerson} onChange={e => set('contactPerson', e.target.value)} placeholder="John Doe" /></div>
            </div>
            <div className="grid-2">
              <div className="input-group"><label className="input-label">Email *</label><input type="email" className="input" required value={form.email} onChange={e => set('email', e.target.value)} placeholder="client@company.com" /></div>
              <div className="input-group"><label className="input-label">Phone</label><input className="input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+20 1xx xxxx xxx" /></div>
            </div>
            <div className="input-group"><label className="input-label">Industry</label><input className="input" value={form.industry} onChange={e => set('industry', e.target.value)} placeholder="e.g. E-commerce, Healthcare..." /></div>
            <div className="input-group"><label className="input-label">Notes</label><textarea className="input" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
            {!isEdit && (
              <>
                <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                    <input type="checkbox" checked={form.createAccount} onChange={e => set('createAccount', e.target.checked)} />
                    Create portal account for this client
                  </label>
                  {form.createAccount && (
                    <div className="input-group" style={{ marginTop: 12, marginBottom: 0 }}>
                      <label className="input-label">Portal Password *</label>
                      <input type="password" className="input" required={form.createAccount} value={form.password} onChange={e => set('password', e.target.value)} placeholder="Set portal login password" />
                    </div>
                  )}
                </div>

                <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>Client Services</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        Choose services, edit prices and details, then print a quotation from the client profile.
                      </div>
                    </div>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={addClientService}>
                      <Plus size={13} /> Add Service
                    </button>
                  </div>
                  {!!servicePackages.length && (
                    <div className="input-group">
                      <label className="input-label">Apply Package</label>
                      <select className="input select" onChange={e => e.target.value && applyPackage(e.target.value)} defaultValue="">
                        <option value="">Choose ready-made package...</option>
                        {servicePackages.map(p => <option key={p.id} value={p.id}>{p.name} - {p.currency} {Number(p.total || 0).toLocaleString()}</option>)}
                      </select>
                    </div>
                  )}

                  {clientServices.map((svc, i) => (
                    <div key={i} style={{ borderTop: i ? '1px solid var(--border)' : 'none', paddingTop: i ? 12 : 0, marginTop: i ? 12 : 0 }}>
                      <div className="grid-2">
                        <div className="input-group">
                          <label className="input-label">Service</label>
                          <select className="input select" value={svc.serviceId} onChange={e => updateClientService(i, 'serviceId', e.target.value)}>
                            <option value="">Custom service</option>
                            {serviceCatalog.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                        </div>
                        <div className="input-group">
                          <label className="input-label">Service Name</label>
                          <input className="input" value={svc.name} onChange={e => updateClientService(i, 'name', e.target.value)} placeholder="Service name" />
                        </div>
                      </div>
                      <div className="input-group">
                        <label className="input-label">Details</label>
                        <textarea className="input" rows={2} value={svc.description || ''} onChange={e => updateClientService(i, 'description', e.target.value)} placeholder="Service details for quotation" />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 120px 32px', gap: 8, alignItems: 'end' }}>
                        <div className="input-group">
                          <label className="input-label">Qty</label>
                          <input className="input" type="number" min="1" step="1" value={svc.quantity} onChange={e => updateClientService(i, 'quantity', e.target.value)} />
                        </div>
                        <div className="input-group">
                          <label className="input-label">Price</label>
                          <input className="input" type="number" min="0" step="0.01" value={svc.unitPrice} onChange={e => updateClientService(i, 'unitPrice', e.target.value)} />
                        </div>
                        <div className="input-group">
                          <label className="input-label">Currency</label>
                          <select className="input select" value={svc.currency} onChange={e => updateClientService(i, 'currency', e.target.value)}>
                            {Object.keys(CURRENCIES).map(code => <option key={code} value={code}>{code}</option>)}
                          </select>
                        </div>
                        <button type="button" onClick={() => removeClientService(i)}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 8 }}>
                          <X size={15} />
                        </button>
                      </div>
                    </div>
                  ))}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, fontSize: 13 }}>
                    <strong>Services Total: {paymentPlan.currency} {servicesTotal.toLocaleString()}</strong>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={useServicesAsPaymentTotal}>
                      Use as payment total
                    </button>
                  </div>
                  {!serviceCatalog.length && (
                    <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)' }}>
                      No saved services yet. You can still add a custom service here, or add reusable services from the Services page.
                    </div>
                  )}
                </div>

                <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: enablePaymentPlan ? 14 : 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <CreditCard size={17} color="var(--accent)" />
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>Client Payment Plan</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Set total contract value and installments while adding the client.</div>
                      </div>
                    </div>
                    <button type="button" onClick={() => setEnablePaymentPlan(v => !v)}
                      className={`btn btn-sm ${enablePaymentPlan ? 'btn-primary' : 'btn-secondary'}`}>
                      {enablePaymentPlan ? 'Enabled' : 'Enable'}
                    </button>
                  </div>
                  {enablePaymentPlan && (
                    <>
                      <div className="grid-2">
                        <div className="input-group">
                          <label className="input-label">Service / Contract Name *</label>
                          <input className="input" required={enablePaymentPlan} value={paymentPlan.serviceName} onChange={e => setPlan('serviceName', e.target.value)} placeholder="e.g. Website Package, Monthly Retainer" />
                        </div>
                        <div className="input-group">
                          <label className="input-label">Currency</label>
                          <select className="input select" value={paymentPlan.currency} onChange={e => setPlan('currency', e.target.value)}>
                            {Object.keys(CURRENCIES).map(code => <option key={code} value={code}>{code}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="input-group">
                        <label className="input-label">Total Contract Value *</label>
                        <input type="number" min="0" step="0.01" className="input" required={enablePaymentPlan} value={paymentPlan.total} onChange={e => setPlan('total', e.target.value)} placeholder="0.00" />
                      </div>
                      <div className="input-group">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <label className="input-label" style={{ marginBottom: 0 }}>Installments</label>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={splitEvenly}>Split Evenly</button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 140px 28px', gap: 8, marginBottom: 8 }}>
                          {['Description', 'Amount', 'Due Date', ''].map(h => <div key={h} style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>{h}</div>)}
                        </div>
                        {paymentPlan.installments.map((inst, i) => (
                          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 140px 28px', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                            <input className="input" style={{ fontSize: 13, padding: '8px 10px' }} value={inst.description} onChange={e => updateInstallment(i, 'description', e.target.value)} placeholder={`Payment ${i + 1}`} />
                            <input type="number" min="0" step="0.01" className="input" style={{ fontSize: 13, padding: '8px 10px' }} value={inst.amount} onChange={e => updateInstallment(i, 'amount', e.target.value)} placeholder="0.00" />
                            <input type="date" className="input" style={{ fontSize: 13, padding: '8px 10px' }} value={inst.dueDate} onChange={e => updateInstallment(i, 'dueDate', e.target.value)} />
                            <button type="button" onClick={() => removeInstallment(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 4 }}><X size={14} /></button>
                          </div>
                        ))}
                        <button type="button" className="btn btn-secondary btn-sm" onClick={addInstallment}><Plus size={13} /> Add Installment</button>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 12px', borderRadius: 8, background: Math.abs(planDiff) < 0.01 ? 'rgba(34,197,94,0.08)' : 'rgba(234,179,8,0.08)', border: `1px solid ${Math.abs(planDiff) < 0.01 ? 'rgba(34,197,94,0.25)' : 'rgba(234,179,8,0.3)'}`, fontSize: 13 }}>
                        <span style={{ color: 'var(--text-muted)' }}>Installments Total</span>
                        <strong>{planSymbol}{installmentsTotal.toFixed(2)}</strong>
                        {planTotal > 0 && Math.abs(planDiff) >= 0.01 && <span style={{ color: '#eab308' }}>{planSymbol}{Math.abs(planDiff).toFixed(2)} {planDiff > 0 ? 'remaining' : 'over'}</span>}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
            {isEdit && (
              <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <ShieldCheck size={16} color="var(--accent)" />
                  <div style={{ fontSize: 14, fontWeight: 700 }}>Portal Access</div>
                  {initial.account ? <span className="badge badge-green">Active</span> : <span className="badge badge-gray">No account</span>}
                </div>
                {!initial.account ? (
                  <>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                      <input type="checkbox" checked={form.createAccount} onChange={e => set('createAccount', e.target.checked)} />
                      Create portal account for this client
                    </label>
                    {form.createAccount && (
                      <div className="input-group" style={{ marginTop: 12, marginBottom: 0 }}>
                        <label className="input-label">Portal Password</label>
                        <input type="password" className="input" value={form.portalPassword} onChange={e => set('portalPassword', e.target.value)} placeholder="Leave empty to use Client@123" />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
                      Login email: {initial.account.email}
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                      <input type="checkbox" checked={form.resetPassword} onChange={e => set('resetPassword', e.target.checked)} />
                      Reset portal password
                    </label>
                    {form.resetPassword && (
                      <div className="input-group" style={{ marginTop: 12, marginBottom: 0 }}>
                        <label className="input-label">New Portal Password *</label>
                        <input type="password" className="input" required value={form.portalPassword} onChange={e => set('portalPassword', e.target.value)} placeholder="Set a new password" />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary">{isEdit ? 'Save Changes' : 'Add Client'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const DeleteClientModal = ({ client, onClose, onConfirm }) => {
  const [typed, setTyped] = useState('');
  const matches = typed === client.companyName;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <div className="modal-title">Delete Client</div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 14 }}>
            This will permanently delete <strong>{client.companyName}</strong> and all related projects, tasks, files, and invoices.
          </p>
          <div className="input-group">
            <label className="input-label">Type the company name to confirm</label>
            <input className="input" autoFocus value={typed} onChange={e => setTyped(e.target.value)} placeholder={client.companyName} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn" disabled={!matches} onClick={onConfirm}
              style={{ background: matches ? '#dc2626' : 'var(--bg-hover)', color: matches ? '#fff' : 'var(--text-muted)' }}>
              <Trash2 size={14} /> Delete Permanently
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClientDetailsModal = ({ client, onClose }) => {
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`/api/clients/${client.id}`)
      .then(r => setDetails(r.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [client.id]);

  const projects = details?.projects || [];
  const tasks = projects.flatMap(project => (project.tasks || []).map(task => ({ ...task, project })));
  const invoices = details?.invoices || [];
  const projectFinancials = invoices.reduce((acc, inv) => {
    const key = inv.projectId || 'general';
    if (!acc[key]) acc[key] = { contracts: {}, paid: {}, remaining: {} };
    addMoney(acc[key].contracts, inv.currency, inv.total);
    addMoney(acc[key].paid, inv.currency, invoicePaidAmount(inv));
    addMoney(acc[key].remaining, inv.currency, invoiceRemainingAmount(inv));
    return acc;
  }, {});
  const totals = invoices.reduce((acc, inv) => {
    addMoney(acc.contracts, inv.currency, inv.total);
    addMoney(acc.paid, inv.currency, invoicePaidAmount(inv));
    addMoney(acc.remaining, inv.currency, invoiceRemainingAmount(inv));
    return acc;
  }, { contracts: {}, paid: {}, remaining: {} });

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 900 }}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{client.companyName}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{client.email}</div>
          </div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          {loading ? (
            <div style={{ padding: 24, color: 'var(--text-muted)' }}>Loading...</div>
          ) : (
            <>
              <div className="stats-grid" style={{ marginBottom: 20 }}>
                {[
                  { label: 'Services', value: projects.length, icon: <FolderKanban size={20} />, color: '#3b82f6' },
                  { label: 'Contract Value', value: moneyGroupsText(totals.contracts), icon: <DollarSign size={20} />, color: '#a855f7' },
                  { label: 'Paid', value: moneyGroupsText(totals.paid), icon: <FileText size={20} />, color: '#22c55e' },
                  { label: 'Remaining', value: moneyGroupsText(totals.remaining), icon: <DollarSign size={20} />, color: '#ef4444' },
                ].map(s => (
                  <div key={s.label} className="stat-card" style={{ padding: 16 }}>
                    <div className="stat-icon" style={{ color: s.color, marginBottom: 8 }}>{s.icon}</div>
                    <div className="stat-value" style={{ fontSize: 17 }}>{s.value}</div>
                    <div className="stat-label">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="grid-2">
                <div className="card">
                  <div className="card-title mb-16">Services / Projects</div>
                  {!projects.length ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No services yet</div>
                  ) : projects.map(p => (
                    <div key={p.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                            {(p.type || 'service').replace('_', ' ')} · {p.status?.replace('_', ' ') || 'planning'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 700 }}>{moneyGroupsText(projectFinancials[p.id]?.contracts || {})}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Paid {moneyGroupsText(projectFinancials[p.id]?.paid || {})}</div>
                        </div>
                      </div>
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                        Remaining {moneyGroupsText(projectFinancials[p.id]?.remaining || {})} · {p.progress || 0}% progress
                      </div>
                    </div>
                  ))}
                </div>

                <div className="card">
                  <div className="card-title mb-16">Invoices & Payments</div>
                  {!invoices.length ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No invoices yet</div>
                  ) : invoices.map(inv => {
                    const paid = invoicePaidAmount(inv);
                    const remaining = invoiceRemainingAmount(inv);
                    return (
                      <div key={inv.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{inv.invoiceNumber}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{inv.project?.name || 'General'} · {inv.status}</div>
                          </div>
                          <div style={{ textAlign: 'right', fontWeight: 700 }}>{fmtMoney(inv.total, inv.currency)}</div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                          <div style={{ color: '#22c55e' }}>Paid: {fmtMoney(paid, inv.currency)}</div>
                          <div style={{ color: remaining > 0 ? '#ef4444' : 'var(--text-muted)', textAlign: 'right' }}>Remaining: {fmtMoney(remaining, inv.currency)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="card" style={{ marginTop: 16 }}>
                <div className="card-title mb-16">Linked Tasks</div>
                {!tasks.length ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No tasks yet</div>
                ) : (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr><th>Task</th><th>Project</th><th>Status</th><th>Priority</th><th>Assignee</th><th>Due</th></tr>
                      </thead>
                      <tbody>
                        {tasks.map(task => (
                          <tr key={task.id} onClick={() => { onClose(); navigate(`/projects/${task.projectId}`); }} style={{ cursor: 'pointer' }}>
                            <td>
                              <div style={{ fontWeight: 600 }}>{task.title}</div>
                              {task.description && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{task.description}</div>}
                            </td>
                            <td style={{ color: 'var(--text-secondary)' }}>{task.project?.name}</td>
                            <td><span className="badge badge-blue">{task.status?.replace('_', ' ')}</span></td>
                            <td><span className={`badge ${task.priority === 'urgent' ? 'badge-red' : task.priority === 'high' ? 'badge-yellow' : 'badge-gray'}`}>{task.priority}</span></td>
                            <td style={{ color: 'var(--text-secondary)' }}>{task.assignee?.name || 'Unassigned'}</td>
                            <td style={{ color: 'var(--text-secondary)' }}>{task.dueDate ? new Date(task.dueDate).toLocaleDateString() : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Clients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [detailsModal, setDetailsModal] = useState(null);
  const [search, setSearch] = useState('');
  const { showToast } = useToast();

  const load = () => { axios.get('/api/clients').then(r => setClients(r.data.data)).catch(console.error).finally(() => setLoading(false)); };
  useEffect(load, []);

  const deleteClient = async (client) => {
    try { await axios.delete(`/api/clients/${client.id}`); setDeleteModal(null); showToast('Client deleted', 'success'); load(); }
    catch (err) { showToast(err.response?.data?.message || 'Failed to delete client', 'error'); }
  };

  const filtered = clients.filter(c =>
    c.companyName.toLowerCase().includes(search.toLowerCase()) ||
    c.contactPerson.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading...</div>;

  return (
    <div className="page-content">
      {modal && (
        <ClientModal
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
          initial={modal.client || undefined}
        />
      )}
      {deleteModal && (
        <DeleteClientModal
          client={deleteModal}
          onClose={() => setDeleteModal(null)}
          onConfirm={() => deleteClient(deleteModal)}
        />
      )}
      {detailsModal && (
        <ClientDetailsModal
          client={detailsModal}
          onClose={() => setDetailsModal(null)}
        />
      )}
      <div className="flex-between mb-24">
        <div><h1 style={{ fontSize: 20, fontWeight: 700 }}>Clients</h1><p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{clients.length} total</p></div>
        <button className="btn btn-primary" onClick={() => setModal({})}><Plus size={16} /> Add Client</button>
      </div>
      <div style={{ marginBottom: 20 }}>
        <input className="input" style={{ maxWidth: 340 }} placeholder="Search clients..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      {!filtered.length ? <div className="empty-state"><Building2 /><p>No clients found</p></div> : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Company</th><th>Contact</th><th>Email</th><th>Industry</th><th>Projects</th><th>Portal</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id}>
                    <td><div style={{ fontWeight: 500 }}>{c.companyName}</div></td>
                    <td style={{ color: 'var(--text-secondary)' }}>{c.contactPerson}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{c.email}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{c.industry || '—'}</td>
                    <td><span style={{ fontWeight: 500 }}>{c.projects?.length || 0}</span><span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 4 }}> project{(c.projects?.length || 0) !== 1 ? 's' : ''}</span></td>
                    <td>{c.account ? <span className="badge badge-green">Active</span> : <span className="badge badge-gray">No account</span>}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button onClick={() => navigate(`/clients/${c.id}`)} title="View client profile"
                          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px' }}>
                          <Eye size={15} />
                        </button>
                        <button onClick={() => setModal({ client: c })} title="Edit"
                          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px' }}>
                          <Pencil size={15} />
                        </button>
                        <button onClick={() => setDeleteModal(c)} title="Delete"
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;

